import FAAxios from '../../http/requests/factory-tests';

export default {
    /**
     * Opens factory test report edit modal.
     *
     * @param commit Vuex commit function
     * @param id factory test report ID
     */
    openFactoryTestModal ({ commit }, id) {
        commit('TOGGLE_FACTORY_TEST_EDIT_MODAL', {
            open: true,
            id: id,
            ticket: null,
        });
    },

    /**
     * Closes factory test report edit modal.
     *
     * @param commit Vuex commit function
     */
    closefactoryTestModal ({ commit }) {
        commit('TOGGLE_FACTORY_TEST_EDIT_MODAL', {
            open: false,
            id: null,
            ticket: null,
        });
    },

    /**
     * Creates new or updates existing factory test report.
     *
     * @param commit Vuex commit function
     * @param state Vuex state
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    createUpdateFactoryTestReport ({
        commit,
        state,
    }, payload) {

        let data = JSON.parse(JSON.stringify(payload.report));
        data.newAttachments.forEach(newAttach => {
            data.attachments.push(newAttach);
        })
        delete(data.newAttachments);
        data.completed = payload.completed || false;
        return new Promise((resolve, reject) => {
            FAAxios.createUpdateFactoryTestReport(data)
                .then(response => {
                    commit('SET_FACTORY_TEST_EDIT_MODAL_ERRORS', {});
                    commit('SET_FACTORY_TEST_EDIT_MODAL_DATA', response.data.data);
                    resolve(response);
                })
                .catch(error => {
                    if (error.response.status === 422) {
                        commit('SET_FACTORY_TEST_EDIT_MODAL_ERRORS', error.response.data.data);
                    }

                    reject(error);
                });
        });
    },

    /**
     * Sets factory test report edit modal data.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    setFactoryTestReportData ({ commit }, data) {
        commit('SET_FACTORY_TEST_EDIT_MODAL_DATA', data);
    },

    /**
     * Retrieves factory test report creation information.
     *
     * @param commit Vuex commit function
     *
     * @returns {Promise<unknown>}
     */
    getCreationInformation ({ commit }) {
        return new Promise((resolve, reject) => {
            FAAxios.getCreationInformation()
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('SET_FACTORY_TEST_CREATION_INFO', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Retrieves factory test report list.
     *
     * @param payload Additional filter properties
     *
     * @returns {Promise<unknown>}
     */
    getFactoryTestReportList ({}, payload) {
        return FAAxios.getFactoryTestReportList(payload.httpOptions);
    },

    /**
     * Retrieves factory test report with given ID.
     *
     * @param commit Vuex commit function
     * @param id factory test report ID
     *
     * @returns {Promise<unknown>}
     */
    getFactoryTestReport ({ commit }, id) {
        return new Promise((resolve, reject) => {
            FAAxios.getFactoryTestReport(id)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('SET_FACTORY_TEST_EDIT_MODAL_DATA', response.data.data);
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Triggers factory test report list refresh.
     *
     * @param commit Vuex commit function
     * @param status Should list be refreshed
     */
    toggleFactoryTestReportListRefresh ({ commit }, status) {
        commit('TOGGLE_LIST_REFRESH', status);
    },

    /**
     * Get file upload URL.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    getUploadUrl: function ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            FAAxios.getFileUploadUrl(payload.fileName).then(response => {
                if (!response.data.success) {
                    reject({ message: response.data.message });
                }

                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    getOpenPdfUrl (_, id) {
        return new Promise((resolve, reject) => {
            FAAxios.getOpenPdfUrl(id)
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Add new attachment
     *
     * @param commit Vuex commit function
     * @param state Vuex state
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    addNewAttachment: function ({ commit }, payload) {
        return new Promise((resolve) => {
            commit('APPEND_FACTORY_TEST_NEW_ATTACHMENT', payload);
            resolve(true);
        });
    },

    /**
     * Retrieves factory test report filters information.
     *
     * @param commit Vuex commit function
     *
     * @returns {Promise<unknown>}
     */
    getFiltersInformation ({ commit }) {
        return new Promise((resolve, reject) => {
            FAAxios.getFiltersInformation()
                .then(response => {
                    if (!response.data.success) {
                        reject({ message: response.data.message });
                    }

                    commit('SET_FILTERS_INFO', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /**
     * Sets factory test report list filters data.
     *
     * @param commit Vuex commit function
     * @param state Vuex state
     * @param dispatch Vuex dispatch function
     * @param data Data object
     */
    setFactoryTestReportListFilters ({
        commit,
        state,
        dispatch,
    }, data) {
        commit('SET_FILTERS_DATA', data);

        dispatch('storeFactoryTestReportListFilters', state.factoryTestList.filters);
    },

    /**
     * Opens/closes factory test report list filters sidebar.
     *
     * @param commit Vuex commit function
     * @param status Should sidebar be opened
     */
    toggleFactoryTestReportListFiltersSidebar ({ commit }, status) {
        commit('TOGGLE_FILTER_SIDEBAR', status);
    },

    /**
     * Removes single factory test report list filter.
     *
     * @param commit Vuex commit function
     * @param state Vuex state
     * @param dispatch Vuex dispatch function
     * @param data Data object
     */
    removeFactoryTestReportListFilter ({
        commit,
        state,
        dispatch,
    }, data) {
        commit('REMOVE_FILTER', data);

        dispatch('storeFactoryTestReportListFilters', state.factoryTestList.filters);
    },

    /**
     * Stores factory test report list filters in local storage.
     *
     * @param payload Payload data
     */
    storeFactoryTestReportListFilters ({}, payload) {
        localStorage.setItem('factoryTestReportListFilters', JSON.stringify(payload));
    },

    /**
     * Restores factory test report list filters to the state.
     *
     * @param commit Vuex commit function
     */
    restoreFactoryTestReportListFilters ({ commit }) {
        let filters = localStorage.getItem('factoryTestReportListFilters');
        if (filters === null) {
            return;
        }

        commit('SET_FILTERS_DATA', JSON.parse(filters));
    },

    setReferences ({ commit }, data) {
        commit('SET_REFERENCES', data);
    },
    addTest ({ commit }, id) {
        commit('ADD_TEST', id);
    },
    addTestV2 ({ commit }, id) {
        commit('ADD_TEST_V2', id);
    },
    setOpenTest ({ commit }, id) {
        commit('SET_OPEN_TEST', id);
    },
    setOpenTestV2 ({ commit }, id) {
        commit('SET_OPEN_TEST_V2', id);
    },
    setTestData ({ commit }, data) {
        commit('SET_TEST_DATA', data);
    },
    setTestV2Data ({ commit }, data) {
        commit('SET_TEST_V2_DATA', data);
    },
    toggleConfirmationModal ({ commit }, data) {
        commit('TOGGLE_CONFIRMATION_MODAL', data);
    },
    removeTest ({ commit }, index) {
        commit('REMOVE_TEST', index);
    },
    removeTestV2 ({ commit }, index) {
        commit('REMOVE_TEST_V2', index);
    },
    addTestFile ({ commit }, data) {
        commit('ADD_TEST_FILE', data);
    },
    addTestV2File ({ commit }, data) {
        commit('ADD_TEST_V2_FILE', data);
    },

    removeTestFile ({ commit }, data) {
        commit('REMOVE_TEST_FILE', data);
    },
    removeSavedAttachment ({ commit }, data) {
        commit('REMOVE_SAVED_ATTACHMENT', data);
    },
    removeTestV2File ({ commit }, data) {
        commit('REMOVE_TEST_V2_FILE', data);
    },
    /**
     * Cancels factory test report.
     *
     * @param commit Vuex commit function
     * @param payload Payload data
     *
     * @returns {Promise<unknown>}
     */
    cancelFactoryTestReport ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            FAAxios.cancelFactoryTestReport(payload.id, payload.reason)
                .then(response => {
                    commit('SET_FACTORY_TEST_EDIT_MODAL_DATA', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    if (error.response.status === 422) {
                        commit('SET_ACTION_ERRORS', error.response.data.data);
                    }

                    reject(error);
                });
        });
    },

    completeFactoryTestReport ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            FAAxios.completeFactoryTestReport(payload.id)
                .then(response => {
                    commit('SET_FACTORY_TEST_EDIT_MODAL_DATA', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    if (error.response.status === 422) {
                        commit('SET_ACTION_ERRORS', error.response.data.data);
                    }

                    reject(error);
                });
        });
    },

    approveRejectFactoryTestReport ({ commit }, payload) {
        return new Promise((resolve, reject) => {
            FAAxios.approveRejectFactoryTestReport(payload.id, payload.isApproved, payload.reason)
                .then(response => {
                    commit('SET_FACTORY_TEST_EDIT_MODAL_DATA', response.data.data);

                    resolve(response);
                })
                .catch(error => {
                    if (error.response.status === 422) {
                        commit('SET_ACTION_ERRORS', error.response.data.data);
                    }
                    reject(error);
                });
        });
    },

    /**
     * Opens/closes factory test report action with reason block.
     *
     * @param commit Vuex commit function
     * @param data Data object
     */
    toggleActionWithReasonBlock ({ commit }, data) {
        commit('TOGGLE_ACTION_WITH_REASON', data);
    },

    /**
     * Sets action with reason, reason value.
     *
     * @param commit Vuex commit function
     * @param data Reason
     */
    setActionReason ({ commit }, data) {
        commit('SET_ACTION_REASON', data);
    },
    toggleIsUploading ({ commit }, isUploading) {
        commit('TOGGLE_IS_UPLOADING', isUploading);
    },

    /**
     * Sets base route for factory test report modal
     *
     * @param commit Vuex commit function
     * @param name base route
     */
    setBaseRoute ({ commit }, name) {
        commit('SET_BASE_ROUTE', name);
    },
};
